import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@/lib/format";
import { downloadCsvMatrix } from "@/lib/csv";
import type { ApprovalProgress } from "@/pages/apply/hooks/useApprovalProgress";

interface ApprovalTimelineProps {
  progress: ApprovalProgress | null;
  submittedAt: string;
}

interface TimelineEvent {
  key: string;
  time: string;
  title: string;
  desc: string | null;
  meta: { label: string; value: string }[];
  tone: "primary" | "accent" | "secondary";
  icon: string;
}

const TONE_DOT: Record<string, string> = {
  primary: "bg-primary-500 text-background-50",
  accent: "bg-accent-500 text-accent-950",
  secondary: "bg-secondary-500 text-background-50",
};

const TONE_LINE: Record<string, string> = {
  primary: "bg-primary-200",
  accent: "bg-accent-200",
  secondary: "bg-secondary-200",
};

// 客户侧审批时间轴：把「发起审批 → 逐级通过 / 驳回 → 发证 / 结束」按时间串成一条完整历程。
export default function ApprovalTimeline({ progress, submittedAt }: ApprovalTimelineProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!progress || progress.steps.length === 0) return null;

  const events: TimelineEvent[] = [
    {
      key: "submitted",
      time: submittedAt,
      title: t("apply.timelineSubmitted"),
      desc: null,
      meta: [],
      tone: "secondary",
      icon: "ri-file-add-line",
    },
    {
      key: "start",
      time: progress.createdAt,
      title: t("apply.timelineStart"),
      desc: t("apply.timelineStartDesc", { total: progress.totalSteps }),
      meta: [],
      tone: "primary",
      icon: "ri-flow-chart",
    },
  ];

  progress.steps.forEach((step) => {
    if (!step.decision) return;
    const approved = step.decision.decision === "approved";
    const meta: { label: string; value: string }[] = [
      {
        label: t("apply.approvalDecisionApprover"),
        value: step.decision.approverName ?? t("approvals.unknown"),
      },
    ];
    if (step.decision.note) {
      meta.push({ label: t("apply.approvalDecisionNote"), value: step.decision.note });
    }
    events.push({
      key: `step-${step.order}`,
      time: step.decision.createdAt,
      title: approved
        ? t("apply.timelineStepApproved", { order: step.order, name: step.name })
        : t("apply.timelineStepRejected", { order: step.order, name: step.name }),
      desc: null,
      meta,
      tone: approved ? "primary" : "accent",
      icon: approved ? "ri-check-line" : "ri-close-line",
    });
  });

  if (progress.status === "approved" && progress.completedAt) {
    events.push({
      key: "issued",
      time: progress.completedAt,
      title: t("apply.timelineIssued"),
      desc: t("apply.timelineIssuedDesc"),
      meta: [],
      tone: "primary",
      icon: "ri-award-line",
    });
  } else if (progress.status === "rejected" && progress.completedAt) {
    events.push({
      key: "rejected",
      time: progress.completedAt,
      title: t("apply.timelineRejected"),
      desc: t("apply.timelineRejectedDesc"),
      meta: [],
      tone: "accent",
      icon: "ri-close-circle-line",
    });
  }

  const sorted = [...events].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  const isPending = progress.status === "pending";

  const fileName = `${t("apply.timelineExportFileName")}-${progress.requestId}`;

  // 导出为纯文本：一行一个事件，含时间、事件名、审批人与意见，方便直接贴给支持团队。
  const handleExportText = () => {
    const lines = sorted.map((event) => {
      const meta = event.meta.map((item) => `${item.label}: ${item.value}`).join(" | ");
      const desc = event.desc ? `  (${event.desc})` : "";
      return `${formatDateTime(event.time)}  ${event.title}${desc}${meta ? `  [${meta}]` : ""}`;
    });
    const content = `\uFEFF${t("apply.timelineTitle")}\n\n${lines.join("\n")}\n`;
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 导出为 CSV：列头 + 每个事件一行，便于用表格存档或做台账。
  const handleExportCsv = () => {
    const approverLabel = t("apply.approvalDecisionApprover");
    const noteLabel = t("apply.approvalDecisionNote");
    downloadCsvMatrix(`${fileName}.csv`, [
      [t("apply.timelineColTime"), t("apply.timelineColEvent"), t("apply.timelineColApprover"), t("apply.timelineColNote")],
      ...sorted.map((event) => [
        formatDateTime(event.time),
        event.title,
        event.meta.find((item) => item.label === approverLabel)?.value ?? "",
        event.meta.find((item) => item.label === noteLabel)?.value ?? event.desc ?? "",
      ]),
    ]);
  };

  return (
    <div className="mt-3 rounded-md border border-background-200 bg-background-50 px-3.5 py-3">
      <div className="flex w-full flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex cursor-pointer items-center gap-1.5 text-left text-xs font-medium text-foreground-900"
        >
          <span className="flex h-4 w-4 items-center justify-center text-primary-600">
            <i className="ri-git-commit-line" />
          </span>
          {t("apply.timelineTitle")}
          <span className="rounded-full bg-background-100 px-2 py-0.5 text-[10px] font-medium text-foreground-600">
            {sorted.length}
          </span>
        </button>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={handleExportText}
            title={t("apply.timelineExportTitle")}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2 py-1 text-[11px] font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
          >
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-file-text-line" />
            </span>
            {t("apply.timelineExportText")}
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            title={t("apply.timelineExportTitle")}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2 py-1 text-[11px] font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
          >
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-file-excel-2-line" />
            </span>
            {t("apply.timelineExportCsv")}
          </button>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-[11px] text-primary-700"
          >
            {open ? t("apply.timelineCollapse") : t("apply.timelineToggle")}
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </span>
          </button>
        </div>
      </div>

      {open && (
        <ol className="mt-3.5">
          {sorted.map((event, index) => (
            <li key={event.key} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] ${TONE_DOT[event.tone]}`}
                >
                  <i className={event.icon} />
                </span>
                {(index < sorted.length - 1 || isPending) && (
                  <span className={`mt-1 w-px flex-1 ${TONE_LINE[event.tone]}`} />
                )}
              </div>
              <div className="pb-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-medium text-foreground-950">{event.title}</p>
                  <span className="text-[10px] text-foreground-400">{formatDateTime(event.time)}</span>
                </div>
                {event.desc && <p className="mt-0.5 text-[11px] leading-relaxed text-foreground-600">{event.desc}</p>}
                {event.meta.length > 0 && (
                  <dl className="mt-1 flex flex-col gap-0.5 text-[11px]">
                    {event.meta.map((item) => (
                      <div key={item.label} className="flex items-start gap-1.5">
                        <dt className="shrink-0 text-foreground-500">{item.label}</dt>
                        <dd className="text-foreground-800">{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </li>
          ))}

          {isPending && (
            <li className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-secondary-400 bg-secondary-50 text-secondary-800">
                  <i className="ri-more-line" />
                </span>
              </div>
              <div className="pb-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-medium text-secondary-900">{t("apply.timelinePending")}</p>
                  {progress.escalated && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
                      <i className="ri-arrow-up-double-line" />
                      {t("approvals.escalatedTag")}
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] leading-relaxed text-foreground-600">
                  {t("apply.timelinePendingDesc", {
                    name: progress.currentStepName ?? `#${progress.currentStep}`,
                    current: progress.currentStep,
                    total: progress.totalSteps,
                  })}
                </p>
              </div>
            </li>
          )}
        </ol>
      )}
    </div>
  );
}